import type { KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { TermMonthItem } from '@makemydeal/dr-platform-types';
import { LEASE } from '@makemydeal/dr-platform-types';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { CDL_USER_INTENT } from '@makemydeal/dr-dash-store/constants';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { Badge } from '@interstate/components/Badge';
import { ExclamationTriangleIcon } from '@interstate/components/Icons/ExclamationTriangleIcon';
import { CalculatedColumnBadgeWrapper, TermGridItem } from './styled.components';
import { ENTER_KEY_CODE, getNewTarget, areStringsEqual } from '../common/utils';
import type { IPaymentGridErrorState } from '@makemydeal/dr-dash-types';
import {
    CALCULATED_COLUMN_BADGE_BG_COLOR,
    CALCULATED_COLUMN_BADGE_LABEL,
    CALCULATED_COLUMN_ICON_COLOR,
    GENERIC_LAST_FIELD_CHANGED_ERROR,
    INPUT_INVALID_ERROR_MESSAGE
} from '../../constants';
import { PaymentGridSource, resolveColumnVerdict } from '@makemydeal/dr-offer-redux';
import { useIsAspPaymentGridRedesignActive } from './hooks/useIsAspPaymentGridRedesignActive';
import { isTermColumnLoading, useIsColumnCalculated } from './hooks/useIsColumnCalculated';
import { disabledStyle } from './utils';

interface ITermInputProps {
    term: TermMonthItem;
    isLoading: boolean;
    /** Whether any column in the row is flagged - only then does the row re-anchor its cells. */
    hasCalculatedColumn?: boolean;
}

const TermMonthInput = ({ term, isLoading, hasCalculatedColumn = false }: ITermInputProps) => {
    const dispatch = useDispatch();
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const [termValue, setTermValue] = useState('');
    const paymentGridErrorState: IPaymentGridErrorState = useSelector(offerReduxSelectors.getPaymentGridErrorState);
    const isAspPgRedesignEnabled = useIsAspPaymentGridRedesignActive();
    const isLeaseRedesign = isAspPgRedesignEnabled && offerType === LEASE;
    const columnSources = useSelector(offerReduxSelectors.getPaymentGridColumnSources);
    const decidedColumns = useSelector(offerReduxSelectors.getPaymentGridDecidedColumns);
    const columnView = useSelector(offerReduxSelectors.getPaymentGridColumnView);
    const resolvedSelectedColumn = useSelector(offerReduxSelectors.getResolvedPaymentGridSelectedColumnIndex);
    /** On Lease this input waits only on its own column: a term-less column has to stay fillable while another prices. */
    const isColumnLoading = isTermColumnLoading(columnView, term, isLoading);
    /** A column neither engine could price: shown blank, flagged, and open to a new term or rate. */
    const isColumnUnavailable = Boolean(
        isAspPgRedesignEnabled &&
            term.val &&
            resolveColumnVerdict(decidedColumns, columnSources, term.key, term.val) === PaymentGridSource.Unavailable
    );
    const isColumnFlagged = !isLeaseRedesign && isColumnUnavailable;
    const isColumnCalculated = useIsColumnCalculated()(term.key, term.val, !isColumnLoading);

    useEffect(() => {
        setTermValue(`${term.val ?? ''}`);
    }, [term.val, isColumnLoading]);

    const handleChange = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        return setTermValue(e.target.value);
    };

    const handleBlur = (item: TermMonthItem, e: InterstateOnChangeEvent<TextInputEventValue>) => {
        const value = e.target.value === '' ? null : parseInt(e.target.value);
        if (item.val === value) return;

        logNewRelicPageAction('MV:PaymentGrid - Change term value', {
            mvOfferType: offerType,
            mvFieldEdited: 'mv:payment:grid:term',
            mvDealXgId: dealXgId
        });
        if (isAspPgRedesignEnabled && resolvedSelectedColumn != null) {
            dispatch(offerReduxActionCreators.setPaymentGridSelectedColumn(offerType, resolvedSelectedColumn));
        }

        dispatch(
            offerReduxActionCreators.updatePaymentGridTerm(offerType, item.key, value, CDL_USER_INTENT.PAYMENT_GRID_TEXT_ENTRY)
        );
    };

    const handleKeyDownEvent = (item: TermMonthItem) => (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.code === ENTER_KEY_CODE) {
            const newTarget = getNewTarget(event);
            handleBlur(item, newTarget);
        }
    };

    return (
        <TermGridItem
            xs={3}
            className="payment-grid-item"
            $isCalculated={isColumnCalculated}
            $hasCalculatedColumn={hasCalculatedColumn}
        >
            <NumericInput
                size="sm"
                sx={{
                    width: '100%',
                    ...(isColumnLoading && disabledStyle)
                }}
                data-testid={`term-month-input-${term.key}`}
                name={'term-month'}
                label="Term (mo)"
                disabled={isColumnLoading}
                value={termValue}
                allowDecimal={false}
                autoInsertCommas={true}
                // A PQ failure sets this before the fallback can retry, so it would flash an error the fallback may resolve.
                hasError={
                    (areStringsEqual(paymentGridErrorState.lastUpdatedInput, `input_term_${term.key}`) &&
                        paymentGridErrorState.hasError &&
                        !isAspPgRedesignEnabled) ||
                    isColumnFlagged
                }
                errorMessage={isColumnFlagged ? GENERIC_LAST_FIELD_CHANGED_ERROR : INPUT_INVALID_ERROR_MESSAGE}
                customErrorMessages={{ exceedsMaxValueRule: INPUT_INVALID_ERROR_MESSAGE }}
                minValue={1}
                maxValue={1000}
                onChange={handleChange}
                onBlur={(e) => handleBlur(term, e)}
                onKeyDown={handleKeyDownEvent(term)}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            />
            {isColumnCalculated && (
                <CalculatedColumnBadgeWrapper>
                    <Badge
                        variant="caution"
                        sx={{
                            backgroundColor: CALCULATED_COLUMN_BADGE_BG_COLOR,
                            maxWidth: '100%',
                            whiteSpace: 'normal',
                            '& > *': { paddingLeft: '6px', paddingRight: '6px' }
                        }}
                        data-testid={`calculated-column-badge-${term.key}`}
                        startIcon={
                            <ExclamationTriangleIcon fontSize="10.5px" color={CALCULATED_COLUMN_ICON_COLOR} aria-hidden={true} />
                        }
                    >
                        {CALCULATED_COLUMN_BADGE_LABEL}
                    </Badge>
                </CalculatedColumnBadgeWrapper>
            )}
        </TermGridItem>
    );
};

export default TermMonthInput;
